import React, { useState, useRef, useEffect } from "react";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import QRCode from "qrcode";
import axios from "axios";

const PreviewDqr = () => {
  const navigate = useNavigate();
  const { id, id2 } = useParams(); // id = dqrid, id2 = dqrcategoryid
  const canvasRef = useRef(null);
  const imgObjRef = useRef(null);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [DQRImage, setDQRImage] = useState([]); // categorydata -> imagedata[]
  const [showPopup, setShowPopup] = useState(false);

  const token = getCookie("token");
  const userid = getCookie("userid");

  const [selectedImage, setSelectedImage] = useState(null);

  // Structure fields from API (defaults)
  const [formData, setFormData] = useState({
    addqr: "",
    imagewidth: "",
    imageheight: "",
    center: "",
    imagepositionx: "",
    imagepositiony: "",
    exampletext: "",
    fontfamily: "",
    fontcolor: "",
    fontsize: "",
    textpositionx: "",
    textpositiony: "",
  });

  const handleClosePopup = () => setShowPopup(false);

  const handlePreview = (e) => {
    e.preventDefault();
    if (!imageLoaded) {
      alert("Please select an image and wait for it to load.");
      return;
    }
    setShowPopup(true);
    drawCanvas(); // ensure draw immediately when opening
  };

  const handleImageClick = (imageSrc) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageSrc;
    img.onload = () => {
      imgObjRef.current = img;
      setImageLoaded(true);
      setSelectedImage(imageSrc);
      drawCanvas();
    };
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const img = imgObjRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext("2d");
    // Clear & white bg
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Base image (fit 320x480)
    ctx.drawImage(img, 0, 0, 320, 480);

    // Text overlay (from structure)
    const text = (formData.exampletext ?? "").toString();
    if (text) {
      const fontSize = parseInt(formData.fontsize || "20", 10) || 20;
      const fontFamily = formData.fontfamily && formData.fontfamily !== "Select" ? formData.fontfamily : "Arial";
      const fontColor = formData.fontcolor || "#000000";
      const posX = parseFloat(formData.textpositionx || "0") || 0;
      const posY = parseFloat(formData.textpositiony || "0") || 0;
      const center =
        formData.center === true ||
        (typeof formData.center === "string" && formData.center.toLowerCase() === "true");

      ctx.font = `${fontSize}px ${fontFamily}`;
      ctx.fillStyle = fontColor;
      ctx.textAlign = center ? "center" : "left";
      ctx.textBaseline = "top";

      const metrics = ctx.measureText(text);
      const textW = metrics.width;
      const textH = fontSize;

      // background for readability
      ctx.fillStyle = "rgba(0,0,0,0.3)";
      const bgX = center ? posX - textW / 2 - 5 : posX - 5;
      ctx.fillRect(bgX, posY - 5, textW + 10, textH + 10);

      ctx.fillStyle = fontColor;
      ctx.fillText(text, posX, posY);
    }

    // QR overlay
    const qrStr = (formData.addqr ?? "").toString().trim();
    if (qrStr) {
      const qrW = parseFloat(formData.imagewidth || "100") || 100;
      const qrH = parseFloat(formData.imageheight || "100") || 100;
      const qx = parseFloat(formData.imagepositionx || "0") || 0;
      const qy = parseFloat(formData.imagepositiony || "0") || 0;

      const qrCanvas = document.createElement("canvas");
      qrCanvas.width = qrW;
      qrCanvas.height = qrH;

      QRCode.toCanvas(
        qrCanvas,
        qrStr,
        { width: qrW, margin: 0 },
        (err) => {
          if (!err) ctx.drawImage(qrCanvas, qx, qy, qrW, qrH);
        }
      );
    }
  };

  // FETCH: Structure (GET) — EXACT endpoint like curl
  useEffect(() => {
    const fetchStructure = async () => {
      try {
        const url = `https://api.dqr-display-tms.eventum.co.in/api/DQRImageStructureOperation/Get/DqrId/DqrCategoryId/${id}/${id2}`;
        const res = await axios.get(url, {
          headers: {
            accept: "text/plain",
            "X-Authorization": token,
            UserId: userid,
          },
        });

        const row = Array.isArray(res.data?.data) ? res.data.data[0] : null;
        if (row) {
          setFormData({
            addqr: row.addqr ?? "",
            imagewidth: row.imagewidth ?? "",
            imageheight: row.imageheight ?? "",
            center: row.center ?? "",
            imagepositionx: row.imagepositionx ?? "",
            imagepositiony: row.imagepositiony ?? "",
            exampletext: row.exampletext ?? "",
            fontfamily: row.fontfamily ?? "",
            fontcolor: row.fontcolor ?? "",
            fontsize: row.fontsize ?? "",
            textpositionx: row.textpositionx ?? "",
            textpositiony: row.textpositiony ?? "",
          });
        }
      } catch (e) {
        console.error("Structure fetch error:", e);
      }
    };
    if (id && id2) fetchStructure();
  }, [id, id2, token, userid]);

  // FETCH: Images for the DQR + Category (POST Search)
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const body = {
          dqrid: id,
          dqrcategoryid: id2,
          returntype: "structure",
          ispageload: false,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: false,
          pagedirection: "first",
        };
        const res = await axios.post(
          "https://api.dqr-display-tms.eventum.co.in/api/DQRInfoOperation/Search",
          body,
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
            },
          }
        );
        if (res.data?.data?.categorydata) {
          setDQRImage(res.data.data.categorydata);
        } else {
          setDQRImage([]);
        }
      } catch (e) {
        console.error("Images fetch error:", e);
      }
    };
    if (id && id2) fetchImages();
  }, [id, id2, token, userid]);

  // Redraw when popup opens or structure changes and image is already loaded
  useEffect(() => {
    if (showPopup && imageLoaded) drawCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup, imageLoaded, formData]);

  const downloadCanvasAsJPG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) saveAs(blob, "canvas-image.jpg");
    }, "image/jpeg", 1.0);
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h3>Preview DQR Image</h3>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Select an image to preview</h3>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {DQRImage.flatMap((cat) => cat.imagedata || []).map((image) => (
                          <div key={image.id} className="col-sm-2 mb-3">
                            <img
                              src={image.filepath}
                              alt={image.name}
                              onClick={() => handleImageClick(image.filepath)}
                              style={{
                                cursor: "pointer",
                                width: "100%",
                                height: 140,
                                objectFit: "cover",
                                borderRadius: 8,
                                border:
                                  selectedImage === image.filepath
                                    ? "2px solid #17a2b8"
                                    : "1px solid #ddd",
                              }}
                              title={image.name}
                            />
                            <div style={{ marginTop: 4, fontSize: 12, textAlign: "center" }}>
                              {image.name}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Applied Structure Data */}
                      {formData.exampletext && (
                        <div className="row mt-3">
                          <div className="col-12">
                            <div className="card">
                              <div className="card-header">
                                <h5>Applied Structure Data</h5>
                              </div>
                              <div className="card-body">
                                <div className="row">
                                  <div className="col-md-6">
                                    <p><strong>Text:</strong> {formData.exampletext}</p>
                                    <p><strong>Font Family:</strong> {formData.fontfamily}</p>
                                    <p><strong>Font Size:</strong> {formData.fontsize}px</p>
                                    <p>
                                      <strong>Font Color:</strong>{" "}
                                      <span style={{ color: formData.fontcolor }}>{formData.fontcolor}</span>
                                    </p>
                                  </div>
                                  <div className="col-md-6">
                                    <p><strong>Text Position:</strong> X: {formData.textpositionx}, Y: {formData.textpositiony}</p>
                                    <p><strong>Center:</strong> {String(formData.center)}</p>
                                    {formData.addqr ? (
                                      <>
                                        <p><strong>QR Data:</strong> {formData.addqr}</p>
                                        <p><strong>QR Size:</strong> {formData.imagewidth} × {formData.imageheight}</p>
                                        <p><strong>QR Position:</strong> X: {formData.imagepositionx}, Y: {formData.imagepositiony}</p>
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="card-footer">
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={handlePreview}
                          disabled={!imageLoaded}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary ml-2"
                          onClick={downloadCanvasAsJPG}
                          disabled={!imageLoaded}
                        >
                          Download as JPG
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Centered popup with canvas */}
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    {showPopup && (
                      <div
                        className="popup"
                        style={{
                          background: "#fff",
                          border: "1px solid #ccc",
                          borderRadius: 8,
                          padding: 20,
                          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                          position: "relative",
                        }}
                      >
                        <span
                          onClick={handleClosePopup}
                          style={{ cursor: "pointer", position: "absolute", right: 12, top: 8, fontSize: 20 }}
                          aria-label="Close"
                          title="Close"
                        >
                          &times;
                        </span>
                        <canvas ref={canvasRef} width="320" height="480" />
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default PreviewDqr;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}
